if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.section;
        this.productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const mainProductForm = this.mainSubmitButton?.closest('product-form');
        this.mainBuyNowButton = mainProductForm?.querySelector('[data-buy-now-trigger]');

        this.stickyAddToCartButton = this.querySelector('[data-sticky-add-to-cart]');
        this.stickyBuyNowButton = this.querySelector('[data-sticky-buy-now]');
        this.stickyAddToCartLabel = this.querySelector('[data-sticky-add-to-cart-label]');
        this.stickyBuyNowLabel = this.querySelector('[data-sticky-buy-now-label]');
        this.stickyTitle = this.querySelector('[data-sticky-title]');
        this.stickyPrice = this.querySelector('[data-sticky-price]');
        this.stickyPriceRegular = this.querySelector('[data-sticky-price-regular]');
        this.stickyPriceCompare = this.querySelector('[data-sticky-price-compare]');
        this.stickyImage = this.querySelector('[data-sticky-image]');

        this.isVisible = false;
        this.isLayoutReady = false;
      }

      connectedCallback() {
        if (!this.productInfo || !this.mainSubmitButton || !this.stickyAddToCartButton) {
          this.remove();
          return;
        }

        this.initVisibilityTracking();
        this.initVariantSync();
        this.initButtonStateObservers();
        this.initActionHandlers();
        this.updateContent();
        this.scheduleInitialVisibilityCheck();

        this.productInfo.addEventListener('product-info:loaded', () => this.updateContent());
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.addToCartObserver?.disconnect();
        this.buyNowObserver?.disconnect();
        window.removeEventListener('scroll', this.updateStickyVisibility);
        window.removeEventListener('resize', this.onResize);
        document.body.classList.remove('sticky-add-to-cart-visible');
        document.documentElement.style.removeProperty('--height-sticky-atc-bar');
      }

      scheduleInitialVisibilityCheck() {
        this.setVisible(false);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.isLayoutReady = true;
            this.updateStickyVisibility();
            this.classList.add('sticky-add-to-cart--ready');
          });
        });
      }

      initVisibilityTracking() {
        this.buyButtonsContainer = this.mainSubmitButton.closest('.product-form__buttons');
        if (!this.buyButtonsContainer) return;

        this.updateStickyVisibility = this.updateStickyVisibility.bind(this);
        this.onResize = () => {
          this.updateStickyVisibility();
          if (this.isVisible) this.updateStickyBarHeight();
        };

        this.intersectionObserver = new IntersectionObserver(this.updateStickyVisibility, {
          threshold: [0, 0.25, 0.5, 0.75, 1],
        });

        this.intersectionObserver.observe(this.buyButtonsContainer);
        window.addEventListener('scroll', this.updateStickyVisibility, { passive: true });
        window.addEventListener('resize', this.onResize);
      }

      updateStickyVisibility() {
        if (!this.buyButtonsContainer || !this.isLayoutReady) return;

        const rect = this.buyButtonsContainer.getBoundingClientRect();
        const scrolledPastButton = rect.bottom < 0;

        this.setVisible(scrolledPastButton);
      }

      initVariantSync() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.sectionId) return;
          this.updateContent(data.variant);
        });
      }

      initButtonStateObservers() {
        this.addToCartObserver = new MutationObserver(() => this.syncAddToCartButtonState());
        this.addToCartObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        if (this.mainBuyNowButton && this.stickyBuyNowButton) {
          this.buyNowObserver = new MutationObserver(() => this.syncBuyNowButtonState());
          this.buyNowObserver.observe(this.mainBuyNowButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.syncButtonStates();
      }

      initActionHandlers() {
        this.stickyAddToCartButton.addEventListener('click', () => {
          if (this.isButtonDisabled(this.mainSubmitButton) || this.mainSubmitButton.classList.contains('loading')) {
            return;
          }

          this.scrollToElementAndTrigger(this.mainSubmitButton, () => this.mainSubmitButton.click());
        });

        if (this.stickyBuyNowButton && this.mainBuyNowButton) {
          this.stickyBuyNowButton.addEventListener('click', () => {
            if (this.isButtonDisabled(this.mainBuyNowButton)) return;

            this.scrollToElementAndTrigger(this.mainBuyNowButton, () => this.mainBuyNowButton.click());
          });
        }
      }

      isButtonDisabled(button) {
        return button.disabled || button.getAttribute('aria-disabled') === 'true';
      }

      getScrollOffset(target) {
        const targetHeight = target.getBoundingClientRect().height;
        return (window.innerHeight - targetHeight) / 2;
      }

      scrollToElementAndTrigger(target, triggerCallback) {
        this.setVisible(false);

        const scrollOffset = this.getScrollOffset(target);
        const targetTop = Math.max(
          0,
          Math.min(
            document.documentElement.scrollHeight - window.innerHeight,
            window.scrollY + target.getBoundingClientRect().top - scrollOffset
          )
        );

        if (Math.abs(window.scrollY - targetTop) <= 10) {
          this.updateStickyVisibility();
          triggerCallback();
          return;
        }

        window.scrollTo({ top: targetTop, behavior: 'smooth' });

        const runTrigger = () => {
          this.updateStickyVisibility();
          triggerCallback();
        };

        if ('onscrollend' in window) {
          window.addEventListener('scrollend', runTrigger, { once: true });
        } else {
          setTimeout(runTrigger, 600);
        }
      }

      syncButtonStates() {
        this.syncAddToCartButtonState();
        this.syncBuyNowButtonState();
      }

      syncAddToCartButtonState() {
        if (!this.stickyAddToCartButton || !this.mainSubmitButton) return;

        const mainLabel = this.mainSubmitButton.querySelector('span:not(.sold-out-message):not(.loading__spinner)');
        if (mainLabel && this.stickyAddToCartLabel) {
          this.stickyAddToCartLabel.textContent = mainLabel.textContent;
        }

        const isDisabled = this.isButtonDisabled(this.mainSubmitButton);
        const isLoading = this.mainSubmitButton.classList.contains('loading');

        this.stickyAddToCartButton.toggleAttribute('disabled', isDisabled);
        if (isDisabled) {
          this.stickyAddToCartButton.setAttribute('aria-disabled', 'true');
        } else {
          this.stickyAddToCartButton.removeAttribute('aria-disabled');
        }

        this.stickyAddToCartButton.classList.toggle('loading', isLoading);
        this.stickyAddToCartButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      syncBuyNowButtonState() {
        if (!this.stickyBuyNowButton || !this.mainBuyNowButton) return;

        const isDisabled = this.isButtonDisabled(this.mainBuyNowButton);
        this.stickyBuyNowButton.toggleAttribute('disabled', isDisabled);

        if (isDisabled) {
          this.stickyBuyNowButton.setAttribute('aria-disabled', 'true');
        } else {
          this.stickyBuyNowButton.removeAttribute('aria-disabled');
        }
      }

      updateContent(variant) {
        this.updateTitle();
        this.updatePrice();
        this.updateImage(variant);
        this.syncButtonStates();
      }

      updateTitle() {
        if (!this.stickyTitle) return;

        const titleElement = this.productInfo.querySelector('.product__title h1');
        if (titleElement) {
          this.stickyTitle.textContent = titleElement.textContent.trim();
        }
      }

      formatStickyPrice(priceText) {
        if (!priceText) return '';
        return priceText.replace(/\s+[A-Za-z]{3}$/, '').trim();
      }

      updatePrice() {
        const priceSource = this.productInfo.querySelector(`#price-${this.sectionId} .price`);
        if (!priceSource || !this.stickyPrice || !this.stickyPriceRegular) return;

        const isOnSale = priceSource.classList.contains('price--on-sale');
        this.stickyPrice.classList.toggle('sticky-add-to-cart__price--on-sale', isOnSale);

        const sourceRegular = this.formatStickyPrice(
          priceSource.querySelector('.price__regular .price-item--regular')?.textContent?.trim()
        );
        const sourceSale = this.formatStickyPrice(
          priceSource.querySelector('.price__sale .price-item--sale')?.textContent?.trim()
        );
        const sourceCompare = this.formatStickyPrice(
          priceSource.querySelector('.price__sale s.price-item--regular')?.textContent?.trim()
        );

        if (isOnSale && sourceSale) {
          this.stickyPriceRegular.textContent = sourceSale;

          if (sourceCompare) {
            if (!this.stickyPriceCompare) {
              const compareElement = document.createElement('span');
              compareElement.className = 'sticky-add-to-cart__price-compare';
              compareElement.setAttribute('data-sticky-price-compare', '');
              this.stickyPrice.appendChild(compareElement);
              this.stickyPriceCompare = compareElement;
            }

            this.stickyPriceCompare.textContent = sourceCompare;
            this.stickyPriceCompare.hidden = false;
          } else if (this.stickyPriceCompare) {
            this.stickyPriceCompare.hidden = true;
          }
        } else {
          this.stickyPriceRegular.textContent = sourceRegular || sourceSale || '';
          if (this.stickyPriceCompare) {
            this.stickyPriceCompare.hidden = true;
          }
        }
      }

      updateImage(variant) {
        if (!this.stickyImage) return;

        const selectedVariant = variant || this.getSelectedVariant();
        const featuredMedia = selectedVariant?.featured_media;
        const activeMediaImage = this.productInfo.querySelector(
          'media-gallery .product__media-item.is-active img, media-gallery .swiper-slide-active img'
        );

        const imageSource = featuredMedia?.preview_image?.src || activeMediaImage?.currentSrc || activeMediaImage?.src;
        if (!imageSource) return;

        const imageUrl = this.buildImageUrl(imageSource, 120);
        const imageUrl80 = this.buildImageUrl(imageSource, 80);
        const imageUrl160 = this.buildImageUrl(imageSource, 160);

        this.stickyImage.src = imageUrl;
        this.stickyImage.srcset = `${imageUrl80} 80w, ${imageUrl} 120w, ${imageUrl160} 160w`;
        this.stickyImage.alt = featuredMedia?.alt || activeMediaImage?.alt || this.stickyImage.alt;
      }

      getSelectedVariant() {
        const selectedVariantJson = this.productInfo
          .querySelector('variant-selects [data-selected-variant]')
          ?.textContent?.trim();

        return selectedVariantJson ? JSON.parse(selectedVariantJson) : null;
      }

      buildImageUrl(src, width) {
        if (!src) return '';

        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', width);
        return url.toString();
      }

      updateStickyBarHeight() {
        document.documentElement.style.setProperty(
          '--height-sticky-atc-bar',
          `${Math.ceil(this.getBoundingClientRect().height)}px`
        );
      }

      setVisible(visible) {
        if (this.isVisible === visible) return;

        this.isVisible = visible;
        this.classList.toggle('sticky-add-to-cart--visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        document.body.classList.toggle('sticky-add-to-cart-visible', visible);

        if (visible) {
          requestAnimationFrame(() => this.updateStickyBarHeight());
        } else {
          document.documentElement.style.removeProperty('--height-sticky-atc-bar');
        }
      }
    }
  );
}
